import { getDropTargets, getRefBoundContainerProps } from './refManager';
import { ElementRef } from './types';
import {
  DropTarget,
  checkDropTargetsParent,
  getDeltas,
  getRowsAndColumnsCount,
} from './utils';
import { getDropTargetsObjects } from './getDropTargetsObjects';
import { getScrollManager } from './getScrollManager';

export const getDnDContext = (
  event: React.MouseEvent,
  containerRef: ElementRef,
  controller: AbortController
) => {
  const containerProps = getRefBoundContainerProps(containerRef);
  const dropTargets = getDropTargets(event, containerRef);
  if (dropTargets.length === 0) return null;
  if (!checkDropTargetsParent(dropTargets)) {
    throw Error('All sortable elements must have the same parent element');
  }
  const dragTarget = dropTargets.find(
    ({ ref: { current }, props: { dragTargetClass } }) => {
      const isContained = current?.contains(event.target as Node);
      if (!isContained || !dragTargetClass) {
        return isContained;
      }
      return Array.from(
        current?.querySelectorAll(`.${dragTargetClass}`) ?? []
      ).some(ele => ele.contains(event.target as Node));
    }
  );

  if (!dragTarget) return null;

  const { dropTargetsObjects, parentBox, availableDimensions } =
    getDropTargetsObjects(dropTargets, dragTarget);

  const dragTragetObject = dropTargetsObjects.find(
    ({ isDragTarget }) => isDragTarget
  )!;
  if (!parentBox) {
    return null;
  }
  const rowAndColumnCount = getRowsAndColumnsCount(dropTargetsObjects);
  const deltas = getDeltas(dropTargetsObjects, rowAndColumnCount);
  const ghostContainer = containerProps.refBoundGetGhostContainer();
  const scrollValues = getScrollManager({
    controller,
    isAutoScroll: containerProps.isAutoScrollRefBound(),
    target: dragTarget.ref.current,
  });

  const {
    box: { x: relativeStartX, y: relativeStartY },
  } = dropTargetsObjects[0];

  return {
    eventPosition: {
      xStart: event.clientX,
      yStart: event.clientY,
      relativeX: event.clientX - relativeStartX,
      currentX: event.clientX - relativeStartX,
      relativeY: event.clientY - relativeStartY,
      currentY: event.clientY - relativeStartY,
    },
    containerRef,
    containerProps,
    dropTargets: dropTargetsObjects,
    dragTarget: { ...dragTragetObject },
    dragTargetPlaceholder: dragTragetObject,
    parentBox,
    controller,
    isActive: false,
    ...rowAndColumnCount,
    ...deltas,
    availableWidth: availableDimensions.availableWidth,
    availableHeight: availableDimensions.availableHeight,
    ghostContainer,
    ...scrollValues,
  };
};

export type DnDContext = ReturnType<typeof getDnDContext> & {
  lastSwapTarget?: DropTarget | null;
};
